import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Skeleton, notification } from 'antd';

import * as PostService from '../../../../services/bb-api/post';
import * as LayoutUtils from './utils';
import type { LayoutPanels, BaseContainer, Container, PostsContainer } from './utils';
import { txt } from '../../../../locales/i18n';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudExclamation } from '@fortawesome/pro-duotone-svg-icons';

export const postsLayoutPanels: LayoutPanels = {
	layoutName: txt('cms.layoutNames.posts'),
	panels: [
		{
			label: txt('cms.panelNames.layout container'),
			formKey: 'container',
			type: 'baseContainerPanel',
		},

		{
			label: txt('cms.panelNames.posts'),
			formKey: 'posts',
			type: 'postsPanel',
		},

		{
			label: txt('cms.panelNames.post container'),
			formKey: 'postContainer',
			type: 'containerPanel',
		},
	],
};

export const postsLayoutValues: PostsLayoutProps = {
	container: LayoutUtils.defaultBaseContainer,
	posts: {
		titleColor: '#000000',
		textColor: '#000000',
		postIds: {
			dk: [],
		},
	},

	postContainer: {
		...LayoutUtils.defaultContainer,
		backgroundColor: '#ffffff',
	},
};

type PostsLayoutProps = {
	container: BaseContainer;
	posts: PostsContainer;
	postContainer: Container;
};

export const PostsLayout = (props: PostsLayoutProps) => {
	const region = 'dk';
	const postIds = props.posts.postIds[region];

	return (
		<PostsLayoutPreviewStyled
			styledProps={props}
			{...LayoutUtils.getAnimationProps(props.container.animation)}
		>
			{postIds.map((postId) => (
				<Post key={postId} postId={postId} />
			))}

			{postIds.length === 0 && (
				<div className="post-container">
					<Skeleton.Image />
					<Skeleton.Input size="large" />
					<Skeleton.Input size="small" />
				</div>
			)}
		</PostsLayoutPreviewStyled>
	);
};

const Post = ({ postId }: { postId: number }) => {
	type Post = { filename: string; title: string; body: string };
	const [post, setPost] = useState<Post | null>(null);
	const [error, setError] = useState(false);

	useEffect(() => {
		const getPost = async () => {
			setError(false);

			try {
				const post: Post = await PostService.get(postId, { preview: true });
				setPost(post);
			} catch (error) {
				const message = txt('cms.notifications.post not found', { postId });
				notification.warn({ message });
				setPost(null);
				setError(true);
			}
		};

		getPost();
	}, [postId]);

	if (error) {
		return (
			<div className="post-container error">
				<div className="error-container">
					<FontAwesomeIcon icon={faCloudExclamation} />
					<span className="post-id-container">{postId}</span>
				</div>
			</div>
		);
	}

	if (post === null) {
		return (
			<div className="post-container">
				<Skeleton.Image />
				<Skeleton.Input active size="large" />
				<Skeleton.Input active size="small" />
			</div>
		);
	}

	return (
		<div className="post-container">
			<img src={'https://filer.boblberg.dk/Img/Bobler/' + post.filename} alt="lorem ipsum" />
			<h3 className="post-title">{post.title}</h3>
			<p className="post-body">{post.body}</p>
		</div>
	);
};

const PostsLayoutPreviewStyled = styled.div<{ styledProps: PostsLayoutProps }>`
	display: flex;
	justify-content: center;
	align-items: center;
	flex-wrap: wrap;
	padding: 3em;
	gap: 2rem;
	background-color: ${({ styledProps }) => styledProps.container.backgroundColor};
	min-height: ${({ styledProps }) => (styledProps.container.fullscreen ? '100dvh' : 'auto')};

	.button-container {
		display: flex;
		gap: 2ch;
	}

	background-size: cover;
	background-position: center;
	background-image: ${({ styledProps }) => {
		if (styledProps.container.coverSource === '') return 'none';
		return `url(${LayoutUtils.getImageSource(styledProps.container.coverSource)})`;
	}};

	.post-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 0.6rem;

		${({ styledProps }) => LayoutUtils.getContainerStyle(styledProps.postContainer)};
		text-align: center;
		box-shadow: 0 0 1em #aaa3;
		padding: 4em 2em;
		width: 360px;
		height: 520px;

		.ant-skeleton {
			width: 100%;

			:has(.ant-skeleton-image) {
				aspect-ratio: 1;
			}

			.ant-skeleton-image {
				width: 100%;
				height: 100%;
				border-radius: 50%;

				svg {
					zoom: 2;
				}
			}
		}

		img {
			width: 100%;
			aspect-ratio: 1;
			border-radius: 200px;
			object-fit: cover;
		}

		.post-title {
			color: ${({ styledProps }) => styledProps.posts.titleColor};
			padding: 0.2rem;
			overflow: hidden;
			display: -webkit-box;
			-webkit-line-clamp: 1;
			-webkit-box-orient: vertical;
		}

		.post-body {
			color: ${({ styledProps }) => styledProps.posts.textColor};
			padding: 0.2rem;
			overflow: hidden;
			display: -webkit-box;
			-webkit-line-clamp: 3;
			-webkit-box-orient: vertical;
		}
	}

	.post-container.error {
		display: grid;
		place-content: center;

		.error-container {
			font-size: 6rem;
			background-color: white;
			height: 256px;
			width: 256px;
			border: 8px solid tomato;
			border-radius: 50%;

			display: grid;
			place-content: center;

			color: tomato;

			.post-id-container {
				font-size: 2rem;
			}
		}
	}
`;
